<template>
  <transition name="el-notification-fade">
    <div
      :class="['el-notification', 'el-notification--center', customClass, horizontalClass, backgroundClass, {'el-notification--with-progress': duration > 0 }]"
      v-show="visible"
      :style="positionStyle"
      @mouseenter="clearTimer()"
      @mouseleave="startTimer()"
      @click="click"
      role="alert"
      data-test="notification"
    >
      <i
        class="el-notification__icon"
        :class="[ typeClass, iconClass ]"
        v-if="type || iconClass"
      />
      <div
        class="el-notification__group ml-8"
        :class="{ 'is-with-icon': typeClass || iconClass }"
      >
        <div
          class="el-notification__title"
          v-text="title"
        />
        <div
          class="el-notification__content mt-2"
          v-show="message"
        >
          <slot>
            <p v-if="!dangerouslyUseHTMLString">
              {{ message }}
            </p>
            <p
              v-else
              v-html="message"
              @click="callback"
            />
          </slot>
        </div>

        <div
          class="el-notification__closeBtn el-icon-close close-button"
          v-if="showClose"
          @click.stop="close"
        />
      </div>

      <Progress
        :percentage="progress"
        :show-text="false"
        :stroke-width="2"
        class="notification__progress-bar"
        color="#ffffff"
        define-back-color="transparent"
      />
    </div>
  </transition>
</template>

<script type="text/babel">
const typeMap = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
};

const iconTypeMap = {
  success: 'check-circle',
  info: 'info-new',
  warning: 'warning',
  error: 'cross-circle',
};

export default {
  data() {
    return {
      visible: false,
      title: '',
      message: '',
      duration: 4500,
      type: '',
      showClose: true,
      customClass: '',
      iconClass: '',
      onClose: null,
      onClick: null,
      closed: false,
      verticalOffset: 0,
      timer: null,
      dangerouslyUseHTMLString: true,
      position: 'top-right',
      progress: 0,
      intervalId: null,
      startTime: null,
      callback: () => {},
    };
  },

  computed: {
    typeClass() {
      return this.type && iconTypeMap[this.type] ? `icon icon-${iconTypeMap[this.type]}` : '';
    },
    backgroundClass() {
      return this.type && typeMap[this.type] ? `notification--${typeMap[this.type]}` : '';
    },
    horizontalClass() {
      return this.position.indexOf('right') > -1 ? 'right' : 'left';
    },
    verticalProperty() {
      return /^top-/.test(this.position) ? 'top' : 'bottom';
    },
    positionStyle() {
      return {
        [this.verticalProperty]: `${this.verticalOffset}px`,
      };
    },
  },

  methods: {
    destroyElement() {
      clearInterval(this.intervalId);
      this.$el.removeEventListener('transitionend', this.destroyElement);
      this.$destroy(true);
      this.$el.parentNode.removeChild(this.$el);
    },
    click() {
      if (typeof this.onClick === 'function') {
        this.onClick();
      }
    },
    close() {
      this.closed = true;
      this.progress = 100;
      clearInterval(this.intervalId);

      if (typeof this.onClose === 'function') {
        this.onClose();
      }
    },
    clearTimer() {
      clearTimeout(this.timer);
      clearInterval(this.intervalId);
    },
    startTimer() {
      if (this.duration > 0) {
        this.startTime = Date.now();

        clearInterval(this.intervalId);

        const updateInterval = 100;
        this.intervalId = setInterval(() => {
          const elapsed = Date.now() - this.startTime;

          this.progress = Math.min((elapsed / this.duration) * 100, 100);
        }, updateInterval);

        this.timer = setTimeout(() => {
          if (!this.closed) {
            this.close();
          }
        }, this.duration);
      }
    },
    keydown(e) {
      if (e.keyCode === 46 || e.keyCode === 8) {
        this.clearTimer();
      } else if (e.keyCode === 27) {
        if (!this.closed) {
          this.close();
        }
      } else {
        this.startTimer();
      }
    },
  },

  watch: {
    closed(newVal) {
      if (newVal) {
        this.visible = false;
        this.$el.addEventListener('transitionend', this.destroyElement);
      }
    },
  },
  mounted() {
    if (this.duration > 0) {
      this.startTimer();
    }
    document.addEventListener('keydown', this.keydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.keydown);
  },
};
</script>

<style lang="scss" scoped>
:deep() {
  .el-notification__content {
    a {
      color: $--color-white;
      font-weight: 600;
    }
  }
}

.el-notification {
  padding: 12px 26px 12px 16px;
  font-size: 13px;
}

.el-notification--center {
  align-items: center;
}

.el-notification--with-progress {
  padding-bottom: 22px;
}

.el-notification__title,
.el-notification__content {
  color: $--color-white;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
}

.el-notification__icon,
.close-button {
  color: $--color-white;
  line-height: 24px;
}

.close-button {
  top: 8px;
  right: 8px;
  font-size: 18px;
}

@mixin notification-variant($color) {
  background-color: $color;
  border-color: $color;
  color: $--color-white;
}

.notification--success {
  @include notification-variant($--color-success);
}

.notification--info {
  @include notification-variant($--color-primary);
}

.notification--warning {
  @include notification-variant($--color-warning);
}

.notification--error {
  @include notification-variant($--color-danger);
}

.notification__progress-bar {
  position: absolute;
  width: calc(100% - 32px);
  bottom: 12px;
}
</style>
